import React, { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  createOffer,
  deleteOfferImage,
  getMyOffer,
  MAX_OFFER_DESCRIPTION,
  MAX_OFFER_IMAGES,
  MAX_OFFER_TERMS,
  Offer,
  OFFER_TYPES,
  OfferInput,
  reorderOfferImages,
  saveOffer,
  uploadOfferImage,
  venueImageUrl, OfferDraft } from '../../api';
import { ACCEPTED_PHOTO_TYPES, errorMessage, Notice, NoticeBox, photoProblem, Toggle, usePortal } from './shared';
import { DISCOUNT_TYPES, isAlcoholCategory, OFFER_CATEGORIES, useFeatures, WEEK_DAYS } from '../../features';

interface FormState {
  title: string;
  category: string;
  // Dining-bill offers: 'yes' | 'no' | '' (not answered, counts as yes).
  coversAlcohol: string;
  discountType: string;
  discountValue: string;
  activeDays: string[];
  confirmAccuracy: boolean;
  offerType: string;
  benefit: string;
  description: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  maxClaims: string;
  claimsPerUser: string;
  minimumSpend: string;
  terms: string;
  isActive: boolean;
  isFeatured: boolean;
}

// One tile in the image gallery: an uploaded image (edit mode) or a picked file waiting for the
// offer's first save (new offers have no id to upload against yet).
interface Tile {
  key: string;
  src: string;
  imageId?: number;
  file?: File;
}

const pad = (n: number) => String(n).padStart(2, '0');
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const EMPTY_FORM: FormState = {
  title: '', category: '', coversAlcohol: '', discountType: 'Percentage', discountValue: '', activeDays: [], confirmAccuracy: false, offerType: OFFER_TYPES[0], benefit: '', description: '', startDate: todayIso(), endDate: '',
  startTime: '', endTime: '', maxClaims: '100', claimsPerUser: '1', minimumSpend: '', terms: '', isActive: true, isFeatured: false
};

const fromOffer = (o: Offer): FormState => ({
  title: o.title,
  category: o.category ?? '',
  coversAlcohol: o.coversAlcohol === true ? 'yes' : o.coversAlcohol === false ? 'no' : '',
  discountType: o.discountType ?? '',
  discountValue: o.discountValue?.toString() ?? '',
  activeDays: o.activeDays ?? [],
  // Asked again every time the editor opens; the API keeps the last confirmation for unchanged offers.
  confirmAccuracy: false,
  offerType: o.offerType,
  benefit: o.benefit ?? '',
  description: o.description,
  startDate: o.startDate ?? '',
  endDate: o.endDate ?? '',
  startTime: o.startTime?.slice(0, 5) ?? '',
  endTime: o.endTime?.slice(0, 5) ?? '',
  maxClaims: o.maxClaims?.toString() ?? '',
  claimsPerUser: o.claimsPerUser.toString(),
  minimumSpend: o.minimumSpend?.toString() ?? '',
  terms: o.terms ?? '',
  isActive: o.isActive,
  isFeatured: o.isFeatured
});

const fromDraft = (d: OfferDraft): FormState => ({
  ...EMPTY_FORM,
  title: d.title,
  category: d.category,
  coversAlcohol: d.coversAlcohol === false ? 'no' : d.coversAlcohol === true ? 'yes' : '',
  discountType: d.discountType,
  discountValue: String(d.discountValue),
  activeDays: d.activeDays,
  offerType: OFFER_TYPES.includes(d.offerType as (typeof OFFER_TYPES)[number]) ? d.offerType : EMPTY_FORM.offerType,
  benefit: d.discountType === 'Percentage' ? `${d.discountValue}% off` : `₹${d.discountValue} off`,
  description: d.description,
  // Run a suggestion as a four-week trial; the partner can change the dates.
  endDate: (() => { const e = new Date(); e.setDate(e.getDate() + 28); return `${e.getFullYear()}-${pad(e.getMonth() + 1)}-${pad(e.getDate())}`; })(),
  startTime: d.startTime ?? '',
  endTime: d.endTime ?? '',
  minimumSpend: d.minimumSpend ? String(d.minimumSpend) : '',
  terms: d.terms ?? ''
});

// <input type="time"> gives "HH:mm"; the API's TimeOnly wants "HH:mm:ss".
const apiTime = (t: string) => (t ? (t.length === 5 ? `${t}:00` : t) : null);
const optionalNumber = (v: string) => (v.trim() === '' ? null : Number(v));

const toInput = (f: FormState, isPublished: boolean): OfferInput => ({
  title: f.title.trim(),
  category: f.category || null,
  coversAlcohol: f.category === 'DiningBill' ? (f.coversAlcohol === 'yes' ? true : f.coversAlcohol === 'no' ? false : null) : null,
  discountType: f.discountType || null,
  discountValue: f.discountType === 'Other' ? null : optionalNumber(f.discountValue),
  activeDays: f.activeDays,
  confirmAccuracy: f.confirmAccuracy,
  offerType: f.offerType,
  benefit: f.benefit.trim() || null,
  description: f.description.trim(),
  startDate: f.startDate || null,
  endDate: f.endDate || null,
  startTime: apiTime(f.startTime),
  endTime: apiTime(f.endTime),
  maxClaims: optionalNumber(f.maxClaims),
  claimsPerUser: Number(f.claimsPerUser) || 1,
  minimumSpend: optionalNumber(f.minimumSpend),
  terms: f.terms.trim() || null,
  isPublished,
  isActive: f.isActive,
  isFeatured: f.isFeatured
});

const move = <T,>(list: T[], from: number, to: number): T[] => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

// Why a saved offer is (or isn't) visible to customers.
function OfferStatusNote({ offer }: { offer: Offer }) {
  const text = offer.status === 'NeedsCategory' ? 'This offer was published before categories existed. Choose a category and update it to show it to customers again.'
    : offer.status === 'Pending' ? 'Waiting for Cheers2Night to review this offer (usually within a day).'
    : offer.status === 'Rejected' ? `Not approved${offer.moderationNote ? `: ${offer.moderationNote}` : '.'} Edit and update it to send it for review again.`
    : offer.status === 'Suspended' ? `Suspended by Cheers2Night${offer.moderationNote ? `: ${offer.moderationNote}` : '.'}`
    : offer.hiddenReason ?? null;
  if (!text) return null;
  const severity = offer.status === 'Rejected' || offer.status === 'Suspended' ? 'error' : 'info';
  return <div style={{ padding: '14px 20px 0' }}><NoticeBox notice={{ severity, text }} /></div>;
}

interface Props {
  offerId: number | null;
  onClose: () => void;
  // After a save. openOfferId keeps the editor on a just-created offer (e.g. some images failed).
  onSaved: (notice: Notice, openOfferId?: number) => void;
  initialNotice?: Notice;
  // New offer pre-filled from an Offer Assistant suggestion.
  draft?: OfferDraft | null;
}

// "Add New Offer" / "Edit Offer" panel shown beside the offer list on My Offers.
const OfferEditor: React.FC<Props> = ({ offerId, onClose, onSaved, initialNotice = null, draft = null }) => {
  const isEdit = offerId !== null;
  const { account } = usePortal();
  const features = useFeatures();

  const [form, setForm] = useState<FormState>(() => (draft && !isEdit ? fromDraft(draft) : EMPTY_FORM));
  const [offer, setOffer] = useState<Offer | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pending, setPending] = useState<Tile[]>([]);
  const [notice, setNotice] = useState<Notice>(initialNotice);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (offerId === null) return;
    getMyOffer(offerId)
      .then((o) => {
        setOffer(o);
        setForm(fromOffer(o));
      })
      .catch(() => setLoadFailed(true));
  }, [offerId]);

  // Release preview blob URLs when the panel closes.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  useEffect(() => () => pendingRef.current.forEach((p) => URL.revokeObjectURL(p.src)), []);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((prev) => ({ ...prev, [key]: value }));

  const tiles: Tile[] = isEdit
    ? (offer?.images ?? []).map((img) => ({ key: `img-${img.id}`, src: venueImageUrl(img.url) ?? '', imageId: img.id }))
    : pending;

  const addFiles = async (files: File[]) => {
    if (files.length === 0) return;
    const problems: string[] = [];
    const room = MAX_OFFER_IMAGES - tiles.length;
    if (files.length > room) problems.push(`Up to ${MAX_OFFER_IMAGES} images per offer; ${files.length - Math.max(room, 0)} not added.`);
    const accepted = files.slice(0, Math.max(room, 0)).filter((file) => {
      const problem = photoProblem(file);
      if (problem) problems.push(problem);
      return !problem;
    });

    if (!isEdit) {
      setPending((prev) => [...prev, ...accepted.map((file, i) => ({ key: `new-${Date.now()}-${i}`, src: URL.createObjectURL(file), file }))]);
      setNotice(problems.length ? { severity: 'error', text: problems.join(' ') } : null);
      return;
    }

    setUploading(true);
    let added = 0;
    // Sequential so the server-side limit applies in order and errors map to files.
    for (const file of accepted) {
      try {
        const result = await uploadOfferImage(offerId!, file);
        if (result.offer) setOffer(result.offer);
        added++;
      } catch (err) {
        problems.push(`${file.name}: ${errorMessage(err, 'upload failed.')}`);
      }
    }
    setUploading(false);
    setNotice(problems.length
      ? { severity: added ? 'info' : 'error', text: `${added} image(s) added. ${problems.join(' ')}` }
      : { severity: 'success', text: `${added} image(s) added.` });
  };

  const removeTile = async (index: number) => {
    const tile = tiles[index];
    if (!isEdit) {
      URL.revokeObjectURL(tile.src);
      setPending((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    if (!window.confirm('Delete this image?')) return;
    try {
      const result = await deleteOfferImage(offerId!, tile.imageId!);
      if (result.offer) setOffer(result.offer);
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Could not delete the image.') });
    }
  };

  // Drag-and-drop reordering; the first image is the cover.
  const reorder = async (from: number, to: number) => {
    if (from === to) return;
    if (!isEdit) {
      setPending((prev) => move(prev, from, to));
      return;
    }
    const previous = offer!;
    const images = move(previous.images, from, to).map((img, i) => ({ ...img, isCover: i === 0 }));
    setOffer({ ...previous, images });
    try {
      const result = await reorderOfferImages(offerId!, images.map((img) => img.id));
      if (result.offer) setOffer(result.offer);
    } catch (err) {
      setOffer(previous);
      setNotice({ severity: 'error', text: errorMessage(err, 'Could not save the image order.') });
    }
  };

  const handleSave = async (publish: boolean) => {
    if (!form.title.trim()) {
      setNotice({ severity: 'error', text: 'Offer title is required.' });
      return;
    }
    if (publish && !form.confirmAccuracy && (!offer?.partnerConfirmedAt || !isEdit)) {
      setNotice({ severity: 'error', text: 'Tick the confirmation that the offer is accurate and complies with applicable law to publish it.' });
      return;
    }
    if (publish && !form.category) {
      setNotice({ severity: 'error', text: 'Choose what the offer is about (category) to publish it.' });
      return;
    }
    if (form.description.length > MAX_OFFER_DESCRIPTION || form.terms.length > MAX_OFFER_TERMS) {
      setNotice({ severity: 'error', text: 'Shorten the description or terms to fit the character limit.' });
      return;
    }
    setSaving(true);
    setNotice(null);
    try {
      const input = toInput(form, publish);
      if (isEdit) {
        const result = await saveOffer(offerId!, input);
        onSaved({ severity: 'success', text: result.message });
        return;
      }

      const result = await createOffer(input);
      const created = result.offer!;
      const failed: string[] = [];
      // Uploaded in gallery order, so the first tile becomes the cover.
      for (const tile of pending) {
        try {
          await uploadOfferImage(created.id, tile.file!);
        } catch (err) {
          failed.push(`${tile.file!.name}: ${errorMessage(err, 'upload failed.')}`);
        }
      }
      if (failed.length) {
        onSaved({ severity: 'info', text: `${result.message} Some images were not uploaded: ${failed.join(' ')}` }, created.id);
        return;
      }
      onSaved({ severity: 'success', text: result.message });
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Could not save the offer. Please try again.') });
      setSaving(false);
    }
  };

  const header = (
    <div className="pp-editor-head">
      <h3>{isEdit ? 'Edit Offer' : 'Add New Offer'}</h3>
      <button type="button" className="pp-icon-btn" onClick={onClose} aria-label="Close"><CloseIcon /></button>
    </div>
  );

  if (isEdit && loadFailed) {
    return <section className="pp-editor">{header}<div className="pp-placeholder"><p className="pp-muted">This offer could not be found. It may have been deleted.</p></div></section>;
  }
  if (isEdit && !offer) {
    return <section className="pp-editor">{header}<div className="pp-placeholder"><p className="pp-muted">Loading offer...</p></div></section>;
  }

  const busy = saving || uploading;
  const publishLabel = isEdit && offer!.isPublished ? 'Update Offer' : 'Publish Offer';
  const draftLabel = isEdit && offer!.isPublished ? 'Move to Draft' : 'Save as Draft';
  const full = tiles.length >= MAX_OFFER_IMAGES;

  return (
    <form className="pp-editor" onSubmit={(e) => { e.preventDefault(); handleSave(true); }} aria-label={isEdit ? 'Edit offer' : 'Add new offer'}>
      {header}
      <div style={{ padding: notice ? '14px 20px 0' : 0 }}><NoticeBox notice={notice} /></div>
      {isEdit && offer && <OfferStatusNote offer={offer} />}

      <div className="pp-editor-cols">
        <div className="pp-editor-col">
          <div className="pp-field">
            <label htmlFor="offer-title">Offer Title<span className="pp-req">*</span></label>
            <input id="offer-title" className="pp-input" maxLength={120} value={form.title} placeholder="20% off on food" onChange={(e) => set('title', e.target.value)} />
          </div>
          <div className="pp-field">
            <label htmlFor="offer-category">Category<span className="pp-req">*</span></label>
            <select id="offer-category" className="pp-input" value={form.category} onChange={(e) => set('category', e.target.value)}>
              <option value="">Choose what the offer is about</option>
              {OFFER_CATEGORIES.map((c) => {
                // Dining-bill offers can exclude alcohol, so they stay selectable.
                const unavailable = features.disabledCategories.includes(c.value)
                  || (c.value !== 'DiningBill' && isAlcoholCategory(c.value) && !features.alcoholSubmissions);
                return (
                  <option key={c.value} value={c.value} disabled={unavailable && form.category !== c.value}>
                    {c.label}{unavailable ? ' (not available)' : ''}
                  </option>
                );
              })}
            </select>
            <span className="pp-hint" style={{ marginTop: 4 }}>
              {isAlcoholCategory(form.category, form.category === 'DiningBill' ? (form.coversAlcohol === 'no' ? false : form.coversAlcohol === 'yes' ? true : null) : null)
                ? 'Alcohol-related offers are shown only when Cheers2Night allows them, approves the offer and has verified your liquor licence (Venue Profile).'
                : 'Every offer is checked by Cheers2Night before customers see it.'}
            </span>
          </div>
          {form.category === 'DiningBill' && (
            <div className="pp-field">
              <label htmlFor="offer-covers-alcohol">Does the discount also apply to alcohol on the bill?<span className="pp-req">*</span></label>
              <select id="offer-covers-alcohol" className="pp-input" value={form.coversAlcohol} onChange={(e) => set('coversAlcohol', e.target.value)}>
                <option value="">Choose</option>
                <option value="no">No, alcohol is excluded</option>
                <option value="yes">Yes, it covers alcohol too</option>
              </select>
              <span className="pp-hint">Discounts that cover alcohol (or don't say) follow the alcohol rules and may not be shown.</span>
            </div>
          )}
          <div className="pp-row">
            <div className="pp-field">
              <label htmlFor="offer-discount-type">Discount<span className="pp-req">*</span></label>
              <select id="offer-discount-type" className="pp-input" value={form.discountType} onChange={(e) => set('discountType', e.target.value)}>
                <option value="">Choose</option>
                {DISCOUNT_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            {form.discountType !== 'Other' && form.discountType !== '' && (
              <div className="pp-field">
                <label htmlFor="offer-discount-value">{form.discountType === 'Percentage' ? 'Percent (%)' : 'Amount (₹)'}<span className="pp-req">*</span></label>
                <input id="offer-discount-value" type="number" min={1} max={form.discountType === 'Percentage' ? 100 : undefined} className="pp-input"
                  value={form.discountValue} placeholder={form.discountType === 'Percentage' ? '20' : '200'} onChange={(e) => set('discountValue', e.target.value)} />
              </div>
            )}
          </div>
          <div className="pp-field">
            <label htmlFor="offer-venue">Venue<span className="pp-req">*</span></label>
            <select id="offer-venue" className="pp-input" disabled><option>{account?.name ?? 'Your venue'}</option></select>
          </div>
          <div className="pp-field">
            <label htmlFor="offer-type">Offer Type<span className="pp-req">*</span></label>
            <select id="offer-type" className="pp-input" value={form.offerType} onChange={(e) => set('offerType', e.target.value)}>
              {OFFER_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="pp-field">
            <label htmlFor="offer-benefit">Headline Benefit<span className="pp-req">*</span></label>
            <input id="offer-benefit" className="pp-input" maxLength={60} value={form.benefit} placeholder="20% off the food bill" onChange={(e) => set('benefit', e.target.value)} />
          </div>
          <div className="pp-field">
            <label htmlFor="offer-description">Short Description<span className="pp-req">*</span></label>
            <textarea id="offer-description" className="pp-input" value={form.description} placeholder="20% off all food items, dine-in only..." onChange={(e) => set('description', e.target.value)} />
            <span className={`pp-counter ${form.description.length > MAX_OFFER_DESCRIPTION ? 'over' : ''}`}>{form.description.length}/{MAX_OFFER_DESCRIPTION}</span>
          </div>
          <div className="pp-field">
            <label htmlFor="offer-terms">Detailed Terms & Conditions</label>
            <textarea id="offer-terms" className="pp-input" style={{ minHeight: 96 }} value={form.terms} placeholder={'• Dine-in only\n• Not valid with other offers\n• Venue terms apply'} onChange={(e) => set('terms', e.target.value)} />
            <span className={`pp-counter ${form.terms.length > MAX_OFFER_TERMS ? 'over' : ''}`}>{form.terms.length}/{MAX_OFFER_TERMS}</span>
          </div>
          <div className="pp-toggles">
            <Toggle checked={form.isActive} onChange={(v) => set('isActive', v)} label="Active (Visible to users)" />
            <Toggle checked={form.isFeatured} onChange={(v) => set('isFeatured', v)} label="Featured Offer (Show on top)" />
          </div>
        </div>

        <div className="pp-editor-col">
          <div className="pp-row">
            <div className="pp-field">
              <label htmlFor="offer-start-date">Start Date<span className="pp-req">*</span></label>
              <input id="offer-start-date" type="date" className="pp-input" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
            </div>
            <div className="pp-field">
              <label htmlFor="offer-end-date">End Date<span className="pp-req">*</span></label>
              <input id="offer-end-date" type="date" className="pp-input" min={form.startDate || undefined} value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />
            </div>
          </div>
          <div className="pp-row">
            <div className="pp-field">
              <label htmlFor="offer-start-time">Start Time</label>
              <input id="offer-start-time" type="time" className="pp-input" value={form.startTime} onChange={(e) => set('startTime', e.target.value)} />
            </div>
            <div className="pp-field">
              <label htmlFor="offer-end-time">End Time</label>
              <input id="offer-end-time" type="time" className="pp-input" value={form.endTime} onChange={(e) => set('endTime', e.target.value)} />
            </div>
          </div>
          <fieldset className="pp-field" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="pp-label">Days</legend>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {WEEK_DAYS.map((d) => {
                const on = form.activeDays.includes(d);
                return (
                  <button key={d} type="button" className={`pp-btn pp-sm ${on ? 'pp-gold' : 'pp-outline'}`} aria-pressed={on}
                    onClick={() => set('activeDays', on ? form.activeDays.filter((x) => x !== d) : WEEK_DAYS.filter((x) => x === d || form.activeDays.includes(x)))}>
                    {d}
                  </button>
                );
              })}
            </div>
            <span className="pp-hint">{form.activeDays.length === 0 ? 'Runs every day. Pick days to limit it.' : 'Shown to customers only on these days.'}</span>
          </fieldset>
          {/* Claim limits only matter if offer claiming is ever switched back on; values are kept. */}
          {features.offerClaims && <div className="pp-row">
            <div className="pp-field">
              <label htmlFor="offer-max-claims">Max Claims (Total)</label>
              <input id="offer-max-claims" type="number" min={1} className="pp-input" placeholder="Unlimited" value={form.maxClaims} onChange={(e) => set('maxClaims', e.target.value)} />
            </div>
            <div className="pp-field">
              <label htmlFor="offer-per-user">Claims Per User</label>
              <input id="offer-per-user" type="number" min={1} max={100} className="pp-input" value={form.claimsPerUser} onChange={(e) => set('claimsPerUser', e.target.value)} />
            </div>
          </div>}
          <div className="pp-field">
            <label htmlFor="offer-min-spend">Minimum Spend (Optional)</label>
            <div className="pp-prefix"><span>₹</span><input id="offer-min-spend" type="number" min={0} className="pp-input" placeholder="0" value={form.minimumSpend} onChange={(e) => set('minimumSpend', e.target.value)} /></div>
          </div>

          <div>
            <span className="pp-label">Offer Image Gallery</span>
            <p className="pp-muted" style={{ margin: '-2px 0 8px', fontSize: 13 }}>Upload multiple images (Max {MAX_OFFER_IMAGES}). Drag to reorder; the first image is the cover.</p>
            <div
              className={`pp-drop ${dropActive ? 'drag' : ''}`}
              onDragOver={(e) => { if (dragIndex === null) { e.preventDefault(); setDropActive(true); } }}
              onDragLeave={() => setDropActive(false)}
              onDrop={(e) => { if (dragIndex !== null) return; e.preventDefault(); setDropActive(false); addFiles(Array.from(e.dataTransfer.files)); }}
            >
              <div className="pp-drop-row">
                <CloudUploadOutlinedIcon />
                <div>
                  <div>{uploading ? 'Uploading...' : full ? 'Image limit reached' : 'Drag & drop images here'}</div>
                  <div style={{ marginTop: 6 }}>
                    or{' '}
                    <button type="button" className="pp-btn pp-outline" disabled={busy || full} onClick={() => fileInput.current?.click()}>Choose Files</button>
                  </div>
                </div>
              </div>
              <span className="pp-muted">Supported formats: JPG, PNG, WebP (Max 5MB each)</span>
              <input
                ref={fileInput}
                hidden
                multiple
                type="file"
                accept={ACCEPTED_PHOTO_TYPES.join(',')}
                onChange={(e) => { const files = Array.from(e.target.files ?? []); e.target.value = ''; addFiles(files); }}
              />
            </div>
          </div>

          {tiles.length > 0 && (
            <div className="pp-thumbs">
              {tiles.map((tile, index) => (
                <div
                  key={tile.key}
                  className={`pp-thumb ${index === 0 ? 'cover' : ''} ${dragIndex === index ? 'dragging' : ''} ${overIndex === index && dragIndex !== index ? 'over' : ''}`}
                  draggable
                  onDragStart={(e) => { setDragIndex(index); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragOver={(e) => { if (dragIndex !== null) { e.preventDefault(); setOverIndex(index); } }}
                  onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragIndex !== null) reorder(dragIndex, index);
                    setDragIndex(null);
                    setOverIndex(null);
                  }}
                  title="Drag to reorder"
                >
                  <img src={tile.src} alt={index === 0 ? 'Cover image' : `Offer image ${index + 1}`} />
                  {index === 0 && <span className="pp-thumb-tag">Cover</span>}
                  <button type="button" className="pp-thumb-x" onClick={() => removeTile(index)} aria-label="Remove image"><CloseIcon /></button>
                  <DragIndicatorIcon className="pp-thumb-grip" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>
        <label className="pp-hint" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: 'inherit' }}>
          <input type="checkbox" checked={form.confirmAccuracy} onChange={(e) => set('confirmAccuracy', e.target.checked)} style={{ marginTop: 3 }} />
          <span>
            I confirm this offer is accurate, that my venue will honour it as described, and that it complies with applicable law
            (including excise and advertising rules).{isEdit && offer?.partnerConfirmedAt ? ` Last confirmed ${new Date(offer.partnerConfirmedAt).toLocaleDateString('en-IN')}.` : ''}
          </span>
        </label>
      </div>
      <div className="pp-editor-foot">
        <button type="button" className="pp-btn pp-outline" onClick={onClose}>Cancel</button>
        <button type="button" className="pp-btn pp-gold-outline" disabled={busy} onClick={() => handleSave(false)}>{draftLabel}</button>
        <button type="submit" className="pp-btn pp-gold" disabled={busy}>{saving ? 'Saving...' : publishLabel}</button>
      </div>
    </form>
  );
};

export default OfferEditor;
